import authService from './authService';
import { API_CONFIG } from '../config/api';

interface Twin {
  twin_user_id: string;
  name: string;
  description?: string;
}

interface ChatSession {
  session_id: string;
  title: string;
  twin_user_id: string;
  created_at: string;
  updated_at: string;
}

interface PersonalSource {
  type: 'personal';
  sourceType?: 'email' | 'file' | 'calendar' | 'chat' | string;
  subject?: string;
  title?: string;
  from?: string;
  author?: string;
  date?: string;
  snippet?: string;
  document_id?: string;
  fileName?: string;
  webUrl?: string;
}

interface WebSource {
  type: 'web';
  title: string;
  url: string;
  snippet?: string;
}

type ChatSource = PersonalSource | WebSource;

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  sources?: ChatSource[];
  inputTokens?: number;
  outputTokens?: number;
  responseTimeMs?: number;
}

interface ChatHistory {
  session_id: string;
  messages: Message[];
}

class ChatService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const token = await authService.getIdToken();
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      // Expected during initialization — no auth available yet
      return {};
    }
  }

  async getSessions(twinId?: string, userEmail?: string): Promise<ChatSession[]> {
    try {
      let email = userEmail;
      if (!email) {
        const user = await authService.getUserData();
        email = user.email;
      }
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/sessions/${twinId || email}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.sessions || [];
    } catch (error) {
      return [];
    }
  }

  async createSession(twinId: string, title?: string): Promise<ChatSession> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/sessions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          twin_id: twinId,
          title: title || 'New Conversation'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Create session failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getSessionHistory(sessionId: string): Promise<ChatHistory> {
    try {
      const headers = await this.getAuthHeaders();
      const url = `${API_CONFIG.BASE_URL}/api/session/${sessionId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        return { session_id: sessionId, messages: [] };
      }

      const data = await response.json();
      return {
        session_id: sessionId,
        messages: data.messages || []
      };
    } catch (error) {
      return { session_id: sessionId, messages: [] };
    }
  }

  async sendMessage(sessionId: string, message: string, llmModel: string = 'claude'): Promise<Message> {
    const headers = await this.getAuthHeaders();
    const chatUrl = `${API_CONFIG.BASE_URL}/api/chat`;

    const response = await fetch(chatUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: message,
        conversation_id: sessionId,
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Chat API error ${response.status}: ${errorText}`);
    }

    const data = await response.json();

    return {
      role: 'assistant',
      content: data.response || data.message || 'No response received',
      timestamp: new Date().toISOString(),
      sources: data.sources || [],
      inputTokens: data.input_tokens,
      outputTokens: data.output_tokens,
      responseTimeMs: data.response_time_ms
    };
  }

  async updateSessionTitle(sessionId: string, title: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/session/${sessionId}/title`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ title })
      });

      if (!response.ok) {
        throw new Error(`Update session title failed: ${response.status}`);
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteSession(sessionId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/session/${sessionId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        throw new Error(`Delete session failed: ${response.status}`);
      }
    } catch (error) {
      throw error;
    }
  }
}

export default new ChatService();
export type { Twin, ChatSession, Message, ChatHistory, PersonalSource, WebSource, ChatSource };
